import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { PillFormsGrpcContractService } from '@lyra/app/core/api/grpc/services/pill-forms.service';
import { UserTreatmentsScheduleGrpcContractService } from '@lyra/app/core/api/grpc/services/user-treatments-schedule.service';
import { RouteString } from '@lyra/app/core/enums/route-string';
import { SELECTED_LANG } from '@lyra/app/core/providers/selected-lang.provider';
import { ErrorService } from '@lyra/app/core/services/error.service';
import { TO_ANALYZING, TO_PLAN } from '@lyra/app/core/store/actions/plan.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, of, zip } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { isMiniFlowType, MINI_FLOW_HOME } from '../../../questionnaire.config';
import { Medication } from '../../classes/medications.class';
import * as actions from '../actions/medications.actions';
import { MedicationsSelectService, QuestionnaireSelectService } from '../selectors';
import { IQuestionnaireState } from '../states/questionnaire.state';

@Injectable()
export class MedicationsEffects {
  getPillForms$ = createEffect(() =>
    this._actions$.pipe(
      ofType(actions.GET_PILLS_COMMAND),
      switchMap(() =>
        this._pillFormsGrpcContractService
          .getList({
            Ids: [],
          })
          .pipe(
            map(({ Items }) => actions.GET_PILLS_SUCCESS({ pillForms: Items! })),
            catchError((response) =>
              this._errorService.handleApiError(response, 'PillFormsGrpcContractClient.GetList'),
            ),
          ),
      ),
    ),
  );

  create$ = createEffect(() =>
    this._actions$.pipe(
      ofType(actions.CREATE_COMMAND),
      withLatestFrom(
        this._questionnaireSelect.getType(),
        this._medicationsSelect.getMedications(),
        this._medicationsSelect.takingMedications(),
      ),
      tap(([, type]) => {
        // Mini-flow variants exit straight to survey-passed, so they skip the analyzing screen.
        if (!isMiniFlowType(type)) {
          this._store$.dispatch(TO_ANALYZING());
        }
      }),
      switchMap(([, , medications, takingMedications]) => {
        if (takingMedications === true) {
          return zip(
            ...medications.map((medication) =>
              this._userTreatmentsScheduleGrpcContractService
                .create(Medication.toRequestObject(medication))
                .pipe(
                  catchError((response) =>
                    this._errorService.handleApiError(response, 'UserTreatmentsScheduleGrpcContractClient.Create'),
                  ),
                ),
            ),
          );
        } else {
          return of(undefined);
        }
      }),
      map(() => actions.CREATE_SUCCESS()),
    ),
  );

  createSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(actions.CREATE_SUCCESS),
        withLatestFrom(this._questionnaireSelect.getType(), this._locale$),
        map(([, type, locale]) => {
          // Mini-flow variants land on their own survey-passed page; the full quiz continues to the plan.
          const home = type ? MINI_FLOW_HOME[type] : undefined;
          if (home) {
            this._router.navigate(['/', locale, home, RouteString.SURVEY_PASSED], {
              relativeTo: this._activatedRoute,
              queryParamsHandling: 'merge',
              // change route without adding empty route to the history (like redirectTo)
              replaceUrl: true,
            });
          } else {
            this._store$.dispatch(TO_PLAN());
          }
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private _store$: Store<IQuestionnaireState>,
    private _actions$: Actions,
    private _errorService: ErrorService,
    private _pillFormsGrpcContractService: PillFormsGrpcContractService,
    private _userTreatmentsScheduleGrpcContractService: UserTreatmentsScheduleGrpcContractService,
    private _medicationsSelect: MedicationsSelectService,
    private _questionnaireSelect: QuestionnaireSelectService,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    @Inject(SELECTED_LANG) private _locale$: Observable<string>,
  ) {}
}
